import React, { useState, useMemo } from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
    Table,
    Button,
    Form,
    FormGroup,
    Label,
} from "reactstrap";
import { FileUploader } from "react-drag-drop-files";

import API from "../../utils/API";
import CSVDownload from "../../components/CsvDownload";
import DateRange from "../../components/formfields/DateRange";
import LenderSelectSingle from "../../components/formfields/LenderSelectSingle";
import Loader from "../../components/Loader";
import PublisherSelect from "../../components/formfields/PublisherSelect";
import RCIDByPublisher from "../../components/formfields/RCIDByPublisher";
import SubIDByPublisher from "../../components/formfields/SubIDByPublisher";
import dateFormat from "dateformat";

const LockReport = () => {
    const defaultDate =
        dateFormat(new Date().setDate(new Date().getDate() - 6), "mmm d, yyyy") +
        " to " +
        dateFormat(new Date(), "mmm d, yyyy");

    const defaultFiltrationState = {
        date: defaultDate,
        useMinLockDate: false,
        useMaxLockDate: false,
        publisher: [],
        lender: null,
        rcid: [],
        subid: [],
        viewByPublisher: false,
    };

    const [filtration, setFiltration] = useState(defaultFiltrationState);
    const [isSelectAll, setIsSelectAll] = useState({
        publisher: true,
        lender: false,
        rcid: true,
        subid: true,
    });

    const [fileContent, setFileContent] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const [sortConfig, setSortConfig] = useState({
        key: 'publisher_id',
        direction: 'ascending',
    });

    const changeData = (name, value) => {
        if (isSelectAll[name]) {
            setIsSelectAll(prev => ({ ...prev, [name]: false }));
        }

        if (name === 'publisher') {
            setFiltration(prev => ({ ...prev, publisher: value, rcid: [], subid: [] }));
            setIsSelectAll(prev => ({ ...prev, rcid: true, subid: true }));
        } else {
            setFiltration((prev) => ({ ...prev, [name]: value }));
        }
    };

    const changeNativeData = (e) => {
        const { name, value, type, checked } = e.target;
        setFiltration({
            ...filtration,
            [name]: type === "checkbox" ? checked : value,
        });
    };

    const handleSelectAllChange = (e) => {
        const { name, checked } = e.target;
        setIsSelectAll((prev) => ({ ...prev, [name]: checked }));

        if (checked) {
            if (name === 'publisher') {
                setFiltration(prev => ({ ...prev, publisher: [], rcid: [], subid: [] }));
                setIsSelectAll(prev => ({ ...prev, rcid: true, subid: true }));
            } else if (name === 'lender') {
                setFiltration(prev => ({ ...prev, lender: null }));
            } else {
                setFiltration(prev => ({ ...prev, [name]: [] }));
            }
        }
    };

    const handleFileUpload = (file) => {
        const reader = new FileReader();
        reader.onload = function(event) {
            setFileContent(event.target.result);
            setUploadedFile(file);
        };
        reader.readAsText(file);
    };

    const handleReset = () => {
        setFiltration(defaultFiltrationState);
        setIsSelectAll({ publisher: true, lender: false, rcid: true, subid: true });
        setFileContent(null);
        setUploadedFile(null);
        setData(null);
        setSortConfig({ key: 'publisher_id', direction: 'ascending' });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);

        const payload = {
            ...filtration,
            lender: filtration.lender ? [filtration.lender] : [],
            csvData: fileContent,
        };

        API.post("/admin/lockreport/summary", payload)
            .then((response) => {
                setData(response.data.data);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching lock report:", error);
                setLoading(false);
            });
    };

    const handleSort = (key) => {
        setSortConfig(prevConfig => {
            let direction = 'ascending';
            if (prevConfig.key === key && prevConfig.direction === 'ascending') {
                direction = 'descending';
            }
            return { key, direction };
        });
    };

    const sortedData = useMemo(() => {
        const tableData = data?.reportData || [];
        if (sortConfig.key) {
            return [...tableData].sort((a, b) => {
                const valA = a[sortConfig.key] || '';
                const valB = b[sortConfig.key] || '';
                if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return tableData;
    }, [data?.reportData, sortConfig]);

    const renderTable = () => {
        const getSortIcon = (key) => {
            if (sortConfig.key !== key) return null;
            return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
        };

        if (filtration.viewByPublisher) {
            return (
                <Table responsive hover>
                    <thead style={{ cursor: 'pointer' }}>
                    <tr>
                        <th onClick={() => handleSort('publisher_id')}>Publisher ID{getSortIcon('publisher_id')}</th>
                        <th onClick={() => handleSort('locks')}>Locks{getSortIcon('locks')}</th>
                        <th onClick={() => handleSort('gross')}>Gross Sent{getSortIcon('gross')}</th>
                        <th onClick={() => handleSort('total')}>Net Sent{getSortIcon('total')}</th>
                        <th onClick={() => handleSort('percentage')}>% Percent{getSortIcon('percentage')}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {sortedData.map((item, index) => (
                        <tr key={index} style={{ backgroundColor: item.locks > 0 ? "yellow" : "transparent" }}>
                            <td>{item.publisher_id}</td>
                            <td>{item.locks}</td>
                            <td>{item.gross}</td>
                            <td>{item.total}</td>
                            <td>{item.percentage.toFixed(2)}%</td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            );
        }

        return (
            <Table responsive hover>
                <thead style={{ cursor: 'pointer' }}>
                <tr>
                    <th onClick={() => handleSort('publisher_id')}>Publisher ID{getSortIcon('publisher_id')}</th>
                    <th onClick={() => handleSort('publisher_refer_code')}>Publisher Refer Code{getSortIcon('publisher_refer_code')}</th>
                    <th onClick={() => handleSort('subid')}>Subid{getSortIcon('subid')}</th>
                    <th onClick={() => handleSort('locks')}>Locks{getSortIcon('locks')}</th>
                    <th onClick={() => handleSort('gross')}>Gross Sent{getSortIcon('gross')}</th>
                    <th onClick={() => handleSort('total')}>Net Sent{getSortIcon('total')}</th>
                    <th onClick={() => handleSort('percentage')}>% Percent{getSortIcon('percentage')}</th>
                </tr>
                </thead>
                <tbody>
                {sortedData.map((item, index) => (
                    <tr key={index} style={{ backgroundColor: item.locks > 0 ? "yellow" : "transparent" }}>
                        <td>{item.publisher_id}</td>
                        <td>{item.publisher_refer_code}</td>
                        <td>{item.subid || <>&nbsp;</>}</td>
                        <td>{item.locks}</td>
                        <td>{item.gross}</td>
                        <td>{item.total}</td>
                        <td>{item.percentage.toFixed(2)}%</td>
                    </tr>
                ))}
                </tbody>
            </Table>
        );
    };

    const exportFiltration = {
        ...filtration,
        lender: filtration.lender ? [filtration.lender] : [],
        csvData: fileContent,
    };

    return (
        <Container fluid className="p-0">
            <h3 className="h3 mb-3">Lock Report</h3>

            <Form id="lock-report-form" onSubmit={handleSubmit}>
                <Row form>
                    <Col md={4}>
                        <FormGroup>
                            <Label>List of Locks</Label>
                            <FileUploader
                                handleChange={handleFileUpload}
                                name="file"
                                types={["CSV"]}
                                label={"Upload or drop a CSV file right here"}
                                fileOrFiles={uploadedFile}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <div className="d-flex justify-content-between align-items-center">
                                <Label className="mb-2">Lender ID</Label>
                                {/*
                                <FormGroup check>
                                    <Label className="mb-2" check>
                                        <Input type="checkbox" name="lender" checked={isSelectAll.lender} onChange={handleSelectAllChange} /> All Lenders
                                    </Label>
                                </FormGroup>
                                */}
                            </div>
                            {isSelectAll.lender ? (
                                <Input type="text" disabled placeholder="All Lenders" />
                            ) : (
                                <LenderSelectSingle
                                    name={"lender"}
                                    onChange={changeData}
                                    value={filtration.lender}
                                />
                            )}
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <Label className="mb-2 mr-3">Date Range</Label>
                        <FormGroup check inline>
                            <Label check>
                                <Input type="checkbox" name="useMinLockDate" checked={filtration.useMinLockDate} onChange={changeNativeData} /> (Use Lock Date From)
                            </Label>
                        </FormGroup>
                        <FormGroup check inline>
                            <Label check>
                                <Input type="checkbox" name="useMaxLockDate" checked={filtration.useMaxLockDate} onChange={changeNativeData} /> (Use Lock Date To)
                            </Label>
                        </FormGroup>
                        <DateRange
                            onChange={changeData}
                            name={"date"}
                            value={filtration.date}
                            disabled={filtration.useMinLockDate || filtration.useMaxLockDate}
                        />
                    </Col>
                </Row>
                <Row form>
                    <Col md={4}>
                        <FormGroup>
                            <div className="d-flex justify-content-between align-items-center">
                                <Label className="mb-2">Publisher ID</Label>
                                <FormGroup check>
                                    <Label className="mb-2" check>
                                        <Input type="checkbox" name="publisher" checked={isSelectAll.publisher} onChange={handleSelectAllChange} /> All Publishers
                                    </Label>
                                </FormGroup>
                            </div>
                            {isSelectAll.publisher ? (
                                <Input type="text" disabled placeholder="All Publishers" />
                            ) : (
                                <PublisherSelect
                                    name={"publisher"}
                                    onChange={changeData}
                                    value={filtration.publisher}
                                />
                            )}
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <div className="d-flex justify-content-between align-items-center">
                                <Label className="mb-2">RCID</Label>
                                <FormGroup check>
                                    <Label className="mb-2" check>
                                        <Input type="checkbox" name="rcid" checked={isSelectAll.rcid} onChange={handleSelectAllChange} /> All RCIDs
                                    </Label>
                                </FormGroup>
                            </div>
                            {isSelectAll.rcid || isSelectAll.publisher ? (
                                <Input type="text" disabled placeholder="All RCIDs" />
                            ) : (
                                <RCIDByPublisher
                                    name="rcid"
                                    onChange={changeData}
                                    value={filtration.rcid}
                                    publisher={filtration.publisher}
                                />
                            )}
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <div className="d-flex justify-content-between align-items-center">
                                <Label className="mb-2">Sub ID</Label>
                                <FormGroup check>
                                    <Label className="mb-2" check>
                                        <Input type="checkbox" name="subid" checked={isSelectAll.subid} onChange={handleSelectAllChange} /> All Sub IDs
                                    </Label>
                                </FormGroup>
                            </div>
                            {isSelectAll.subid || isSelectAll.publisher ? (
                                <Input type="text" disabled placeholder="All Sub IDs" />
                            ) : (
                                <SubIDByPublisher
                                    name="subid"
                                    onChange={changeData}
                                    value={filtration.subid}
                                    publisher={filtration.publisher}
                                />
                            )}
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FormGroup check>
                            <Label className="mb-2" check>
                                <Input type="checkbox" name="viewByPublisher" checked={filtration.viewByPublisher} onChange={changeNativeData} />{' '}
                                <strong>View by Publisher</strong>
                            </Label>
                        </FormGroup>
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col>
                        <Button color="primary" type="submit" disabled={!fileContent || !filtration.lender}>Generate Report</Button>{' '}
                        <Button color="secondary" type="button" onClick={handleReset}>Reset</Button>
                    </Col>
                </Row>
            </Form>

            <Row className="mt-3">
                <Col>
                    {loading && <Loader />}
                    {data && !loading && (
                        <Card>
                            <div className="d-flex justify-content-between align-items-center card-header">
                                <h5 className="mb-0">Lock Report Results</h5>
                                <div>
                                    <CSVDownload
                                        url="/admin/lockreport/export"
                                        filename="LockReport"
                                        filtration={exportFiltration}
                                    />
                                </div>
                            </div>
                            {data.reportData && data.reportData.length > 0 ? (
                                <CardBody>
                                    <div className="mb-2">
                                        <strong>Total Locks:</strong> {data.totalLocks}
                                    </div>
                                    <div className="table-responsive">
                                        {renderTable()}
                                    </div>
                                </CardBody>
                            ) : (
                                <CardBody className="text-center">
                                    <h5>Data not found</h5>
                                </CardBody>
                            )}
                        </Card>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default LockReport;